import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import PageTitle from "shared/ui/PageTitle";
import Loading from "shared/ui/Loading";
import {useAuth} from "hooks/useAuth";
import Icon from "shared/ui/Icon";
import BoxContent from "shared/ui/BoxContent";
import GenericButton from "shared/ui/GenericButton";
import DashboardBox from "features/dashboard/components/DashboardBox";
import CustomButton from "shared/ui/CustomButton";
import {APIGetAddresses} from "../api/addresses";
import {APIDeleteAddress} from "../api/address";

interface IAddress {
    id: number;
    name: string;
    phone: string;
    city: string;
    county: string;
    country: string;
    line1: string;
    postal_code: string;
    is_company: boolean;
    company_name?: string;
    company_fiscal_code?: string;
    company_registration?: string;
}

const Addresses = () => {
    const {update} = useAuth();
    const navigate = useNavigate();

    const [loading, setLoading] = useState<boolean>(true);
    const [addresses, setAddresses] = useState<IAddress[]>([]);

    useEffect(() => {
        setLoading(true);
        APIGetAddresses()
            .then((response) => {
                if (response.data.success) {
                    setAddresses(response.data.data.data);
                } else {
                    update("error", {show: true, code: "A002"});
                }
            })
            .catch((err) => {
                update("error", {show: true, code: "A001"});
            })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleEditAddress = (id: number) => {
        navigate(`/adrese/${id}/editare`);
    };

    const handleAddAddress = () => {
        navigate(`/adrese/adauga`);
    };

    const deleteAddressById = (id: string, callback: Function) => {
        update("loading", {show: true, modal: true});
        APIDeleteAddress({id})
            .then((response) => {
                if (response.data.success) {
                    setAddresses(addresses.filter((item) => item.id.toString() !== id.toString()));
                } else {
                    update("error", {show: true, code: "A002"});
                }
                callback();
            })
            .catch(() => update("error", {show: true, code: "A001"}))
            .finally(() => update("loading", {show: false}));
    };

    const handleConfirmDelete = (address: { id: number; name: string; phone: string; city: string; [key: string]: any }) => {
        update("confirm", {
            show: true,
            closable: true,
            title: "Ești sigur că vrei să ștergi această adresă?",
            message: (<>Adresa<b> {address.name}</b> va fi ștearsă!</>),
            returnFunction: (callback: Function) => {deleteAddressById(address.id.toString(), callback)},
            buttonYes: "Șterge",
            buttonNo: "Închide",
        });
    };

    const addressPageTitle = () => {
        return (
            <PageTitle actions={<GenericButton type="add" onClick={handleAddAddress} />}>
                Adresele mele
            </PageTitle>
            )
    };

    if(loading) return (
        <Box component="main">
            {addressPageTitle()}
            <Loading show={true}/>
        </Box>
    );

    if(addresses.length === 0) return (
        <Box component="main">
            {addressPageTitle()}
            <BoxContent>
                <Typography variant="body1">Nicio adresă introdusă în platformă.</Typography>
                <CustomButton
                    onClick={handleAddAddress}
                    buttonType="textIcon"
                    bgcolor="dark"
                    icon={<Icon icon="plus" fixedWidth={true} sx={{fontSize: "1.25rem"}}/>}
                >
                    Adaugă adresă
                </CustomButton>
            </BoxContent>
        </Box>
    )

    return (
        <Box component="main">
            {addressPageTitle()}
            <Grid container spacing={3} direction="row" justifyContent="flex-start" alignItems="stretch" mt={0}>
                {addresses.map((address) => (
                    <Grid item md={12} xl={6} key={address.id}>
                        <DashboardBox
                            type="jsx"
                            number={0}
                            title={address.name}
                            icon="fal map-location-dot"
                            actions={
                                <Box display={{xs: "block", md: "flex"}} sx={{textAlign: "right"}}>
                                    <GenericButton
                                        sxExtra={{margin: {xs: "0 0 10px 0", md: "0 10px 0 0"}}}
                                        type="edit-small"
                                        onClick={() => handleEditAddress(address.id)}
                                    />
                                    <GenericButton type="delete" onClick={() => handleConfirmDelete(address)}/>
                                </Box>
                            }
                        >
                            <Grid container spacing={1.5} alignItems="center">
                                <Grid item xs={12} sm={2} md={2} sx={{textAlign: {xs: "left", sm: "right"}}}>
                                    <Typography variant="body2">Adresă:</Typography>
                                </Grid>
                                <Grid item xs={12} sm={10} md={10}>
                                    {address.line1}, {address.city}, {address.county}, {address.country}
                                </Grid>

                                <Grid item xs={12} sm={2} md={2} sx={{textAlign: {xs: "left", sm: "right"}}}>
                                    <Typography variant="body2">Cod poștal:</Typography>
                                </Grid>
                                <Grid item xs={12} sm={10} md={10}>{address.postal_code}</Grid>

                                <Grid item xs={12} sm={2} md={2} sx={{textAlign: {xs: "left", sm: "right"}}}>
                                    <Typography variant="body2">Telefon:</Typography>
                                </Grid>
                                <Grid item xs={12} sm={10} md={10}>{address.phone}</Grid>

                                {address.is_company && (
                                    <>
                                        <Grid item xs={12} sm={2} md={2} sx={{textAlign: {xs: "left", sm: "right"}}}>
                                            <Typography variant="body2">Nume companie:</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={10} md={10}>{address.company_name}</Grid>

                                        <Grid item xs={12} sm={2} md={2} sx={{textAlign: {xs: "left", sm: "right"}}}>
                                            <Typography variant="body2">CUI:</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={10} md={10}>{address.company_fiscal_code}</Grid>

                                        <Grid item xs={12} sm={2} md={2} sx={{textAlign: {xs: "left", sm: "right"}}}>
                                            <Typography variant="body2">Număr înregistrare:</Typography>
                                        </Grid>
                                        <Grid item xs={12} sm={10} md={10}>{address.company_registration}</Grid>
                                    </>
                                )}
                            </Grid>
                        </DashboardBox>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default Addresses;
